import axiosCall from './interceptor';
import URL from '../helper';

export const PRINT_DEFAULTS_STATUS = {
  GET_SUCCESS: 534,
  UPDATE_SUCCESS: 538,
  TEMPLATE_UPDATE_SUCCESS: 703,
};

export const PRINT_CATALOG_DIMENSION = {
  COLOR_MODE: 'color_mode',
  MAIL_TYPE: 'mail_type',
  ENVELOPE_TYPE: 'envelope_type',
};

export const PRINT_CATALOG_STATUS = {
  LIST_SUCCESS: 7150,
  MUTATION_SUCCESS: 7162,
};

export const getOrganizationIdForPrintCatalog = userDetails => {
  if (!userDetails || typeof userDetails !== 'object') {
    return null;
  }
  const id = userDetails.clientOrganizationId ?? userDetails.organizationId;
  return id != null && id !== '' ? String(id) : null;
};

export const printCatalogFieldToDimension = field => {
  switch (field) {
    case 'colorMode':
      return PRINT_CATALOG_DIMENSION.COLOR_MODE;
    case 'mailType':
      return PRINT_CATALOG_DIMENSION.MAIL_TYPE;
    case 'envelopeType':
      return PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE;
    default:
      return null;
  }
};

export const sortPrintCatalogEntries = entries => {
  if (!Array.isArray(entries)) {
    return [];
  }
  return [...entries].sort((a, b) => {
    const ao = Number(a.sortOrder) || 0;
    const bo = Number(b.sortOrder) || 0;
    if (ao !== bo) {
      return ao - bo;
    }
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });
};

export const getPrintCatalogLabelForValue = (entries, value) => {
  if (!Array.isArray(entries) || value == null || String(value) === '') {
    return null;
  }
  const found = entries.find(e => String(e.value) === String(value));
  if (!found) {
    return null;
  }
  return found.displayLabel != null && found.displayLabel !== '' ? found.displayLabel : found.value;
};

const catalogBaseUrl = organizationId =>
  `${URL.BASE_URL}print/organizations/${organizationId}/catalog`;

/** GET without `dimension` returns the full org catalog in one response. */
export const listOrgPrintCatalog = async (organizationId, dimension) => {
  if (!organizationId) {
    return { statusCode: 400, data: [], errors: [{ description: 'organizationId is required' }] };
  }
  const params = dimension ? { dimension } : {};
  const url = catalogBaseUrl(organizationId);
  const response = await axiosCall('get', url, params);
  return response.data;
};

const CATALOG_DIMENSION_ORDER = [
  PRINT_CATALOG_DIMENSION.COLOR_MODE,
  PRINT_CATALOG_DIMENSION.MAIL_TYPE,
  PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE,
];

const buildEmptyPrintCatalogMap = () => ({
  [PRINT_CATALOG_DIMENSION.COLOR_MODE]: [],
  [PRINT_CATALOG_DIMENSION.MAIL_TYPE]: [],
  [PRINT_CATALOG_DIMENSION.ENVELOPE_TYPE]: [],
});

function partitionOrgPrintCatalogResponse(data) {
  const empty = buildEmptyPrintCatalogMap();
  if (data == null) {
    return empty;
  }
  if (Array.isArray(data)) {
    const map = buildEmptyPrintCatalogMap();
    for (const row of data) {
      const dim = row && row.dimension;
      if (dim != null && Object.prototype.hasOwnProperty.call(map, dim)) {
        map[dim].push(row);
      }
    }
    CATALOG_DIMENSION_ORDER.forEach(dim => {
      map[dim] = sortPrintCatalogEntries(map[dim]);
    });
    return map;
  }
  if (typeof data === 'object') {
    const map = buildEmptyPrintCatalogMap();
    CATALOG_DIMENSION_ORDER.forEach(dim => {
      const list = data[dim];
      map[dim] = sortPrintCatalogEntries(Array.isArray(list) ? list : []);
    });
    return map;
  }
  return empty;
}

export const createOrgPrintCatalogEntry = async (organizationId, payload) => {
  if (!organizationId) {
    return { statusCode: 400, data: null, errors: [{ description: 'organizationId is required' }] };
  }
  const url = catalogBaseUrl(organizationId);
  const response = await axiosCall('post', url, payload);
  return response.data;
};

export const updateOrgPrintCatalogEntry = async (organizationId, entryId, payload) => {
  if (!organizationId || !entryId) {
    return { statusCode: 400, data: null, errors: [{ description: 'organizationId and entryId are required' }] };
  }
  const url = `${catalogBaseUrl(organizationId)}/${entryId}`;
  const response = await axiosCall('put', url, payload);
  return response.data;
};

export const deleteOrgPrintCatalogEntry = async (organizationId, entryId) => {
  if (!organizationId || !entryId) {
    return { statusCode: 400, data: null, errors: [{ description: 'organizationId and entryId are required' }] };
  }
  const url = `${catalogBaseUrl(organizationId)}/${entryId}`;
  const response = await axiosCall('delete', url);
  return response.data;
};

export const fetchOrgPrintCatalogMap = async organizationId => {
  if (!organizationId) {
    return buildEmptyPrintCatalogMap();
  }
  const res = await listOrgPrintCatalog(organizationId);
  if (!res || res.statusCode !== PRINT_CATALOG_STATUS.LIST_SUCCESS) {
    return buildEmptyPrintCatalogMap();
  }
  return partitionOrgPrintCatalogResponse(res.data);
};

export const DEFAULT_ORG_PRINT_DEFAULTS = {
  colorMode: null,
  paperWeight: 80,
  mailType: null,
  envelopeType: null,
  returnEnvelope: false,
};

export const EMPTY_FLAT_TEMPLATE_OVERRIDES = {
  colorMode: null,
  paperWeight: null,
  mailType: null,
  envelopeType: null,
  returnEnvelope: null,
};

export const getOrgPrintDefaults = async () => {
  const url = `${URL.BASE_URL}config/printDefaults`;
  const response = await axiosCall('get', url);
  return response.data;
};

export const updateOrgPrintDefaults = async payload => {
  const url = `${URL.BASE_URL}config/printDefaults`;
  const response = await axiosCall('put', url, payload);
  return response.data;
};

export const resetOrgPrintDefaults = async () => {
  return updateOrgPrintDefaults({ ...DEFAULT_ORG_PRINT_DEFAULTS });
};

export const updateTemplatePrintOverrides = async (templateId, payload) => {
  if (!templateId) {
    return { statusCode: 400, data: null, errors: [{ description: 'templateId is required' }] };
  }
  const url = `${URL.DESIGN_SYSTEM_URL}dynamic/document/${templateId}/printSettingsOverrides`;
  const response = await axiosCall('put', url, payload);
  return response.data;
};

export const resetTemplatePrintOverrides = async templateId => {
  return updateTemplatePrintOverrides(templateId, { ...EMPTY_FLAT_TEMPLATE_OVERRIDES });
};
